import { useState } from 'react'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { ComingSoonBadge } from '@/components/ui/ComingSoonBadge'
import { cn } from '@/lib/utils'
import type { HeatmapCell, HeatmapPanel } from '@/types/analytics'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
interface MetricOption {
  key: 'volume' | 'first_reply' | 'csat'
  label: string
  comingSoon?: boolean
}

const METRICS: MetricOption[] = [
  { key: 'volume', label: 'Volume' },
  { key: 'first_reply', label: 'First reply', comingSoon: true },
  { key: 'csat', label: 'CSAT' },
]

interface WhenCustomersChatHeatmapProps {
  data: HeatmapPanel
}

export function WhenCustomersChatHeatmap({ data }: WhenCustomersChatHeatmapProps) {
  const [metric, setMetric] = useState<MetricOption['key']>('volume')

  const cell = (day: number, hour: number): HeatmapCell | undefined =>
    data.cells.find((c) => c.day === day && c.hour === hour)

  // Volume shades against the busiest cell; CSAT against the 1–5 scale.
  const intensity = (c: HeatmapCell | undefined) => {
    if (!c) return 0
    if (metric === 'csat') return c.csat == null ? 0 : c.csat / 5
    if (data.max === 0) return 0
    return Math.min(1, c.value / data.max)
  }

  const cellTitle = (c: HeatmapCell | undefined, dayLabel: string, hour: number) => {
    if (metric === 'csat') {
      return `${dayLabel} ${hour}:00 — ${c?.csat != null ? `${c.csat.toFixed(1)} ★` : 'no ratings'}`
    }
    return `${dayLabel} ${hour}:00 — ${c?.value ?? 0}`
  }

  return (
    <Card className="p-4">
      <CardHeader className="px-0 pb-3 flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">When customers chat</CardTitle>
          <CardDescription>Hour-of-day × day-of-week · last 4 weeks · {data.timezone}</CardDescription>
        </div>
        <div className="flex items-center gap-1 rounded-md border bg-muted/30 p-0.5">
          {METRICS.map((m) => (
            <button
              key={m.key}
              type="button"
              disabled={m.comingSoon}
              onClick={() => !m.comingSoon && setMetric(m.key)}
              className={cn(
                'px-2 py-1 text-xs rounded flex items-center gap-1.5',
                m.comingSoon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                metric === m.key && !m.comingSoon ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground',
              )}
            >
              {m.label}
              {m.comingSoon && <ComingSoonBadge className="ml-0.5" />}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="px-0">
        <div className="flex gap-2 text-xs">
          <div className="flex flex-col justify-around pt-4 pb-1 text-muted-foreground">
            {DAY_LABELS.map((d) => (
              <div key={d} className="h-4 leading-4">{d}</div>
            ))}
          </div>
          <div className="flex-1">
            <div className="flex justify-between pb-1 text-muted-foreground text-[10px] px-0.5">
              {Array.from({ length: 24 }).map((_, h) => (
                <span key={h} className={h % 2 === 0 ? '' : 'opacity-0'}>{h}</span>
              ))}
            </div>
            <div className="grid grid-rows-7 gap-0.5">
              {DAY_LABELS.map((_, dayIdx) => (
                <div key={dayIdx} className="grid grid-cols-24 gap-0.5" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                  {Array.from({ length: 24 }).map((_, hour) => {
                    const c = cell(dayIdx + 1, hour)
                    const a = intensity(c)
                    return (
                      <div
                        key={hour}
                        title={cellTitle(c, DAY_LABELS[dayIdx], hour)}
                        className="h-4 rounded-sm"
                        style={{ backgroundColor: `rgba(127, 255, 228, ${0.08 + a * 0.85})` }}
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
